import React, { useState, useCallback, useEffect } from "react";
import "./UpdateProduct.scss";
import { useMediaQuery } from "@mui/material";
import { toast } from "react-toastify";
import BlueButton from "../../../components/BlueButton/BlueButton";
import ProductHeader from "./ProductHeader";
import ControlGroup from "./ControlGroup";
import Variations from "./Variations";
import Ranges from "./Ranges";
import api from "../../../utils/api";
import { useParams, useNavigate } from "react-router-dom";

const UpdateProduct = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width:767px)");
  // State to store form data
  const [controlGroupData, setControlGroupData] = useState(null);
  const [globalRangesData, setGlobalRangesData] = useState(null);
  const [featuresData, setFeaturesData] = useState(null);
  const [mainImage, setMainImage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [productData, setProductData] = useState(null);
  const [fetchingProduct, setFetchingProduct] = useState(true);

  // Cross-component interaction state
  const [controlGroupHasDiscount, setControlGroupHasDiscount] = useState(false);
  const [variationsHaveDiscount, setVariationsHaveDiscount] = useState(false);
  const [rangesActive, setRangesActive] = useState(false);
  const [variationsActive, setVariationsActive] = useState(false);

  // Fetch product data on component mount
  useEffect(() => {
    const fetchProductData = async () => {
      try {
        setFetchingProduct(true);
        const response = await api.get(`/productDetails/${id}`);
        if (response.data.status === 200) {
          setProductData(response.data.data);
        } else {
          toast.error("فشل في العثور على المنتج");
          navigate("/products");
        }
      } catch (error) {
        console.error("Error fetching product:", error);
        toast.error("حدث خطأ أثناء تحميل بيانات المنتج");
        navigate("/products");
      } finally {
        setFetchingProduct(false);
      }
    };

    if (id) {
      fetchProductData();
    }
  }, [id, navigate]);

  // Use useCallback to memoize handler functions
  const handleControlGroupData = useCallback((formData) => {
    setControlGroupData(formData);
  }, []);

  const handleGlobalRangesData = useCallback((formData) => {
    setGlobalRangesData(formData);
  }, []);

  const handleFeaturesData = useCallback((formData) => {
    setFeaturesData(formData);
  }, []);

  const handleMainImage = useCallback((imageFile) => {
    setMainImage(imageFile);
  }, []);

  // Validate variation fields before submission
  const validateVariations = () => {
    if (!featuresData) return true;

    let i = 0;
    // If variation_type[0] is not present at all, variations are disabled — OK
    if (!featuresData.has(`variation_type[${i}]`)) return true;

    while (featuresData.has(`variation_type[${i}]`)) {
      const type = featuresData.get(`variation_type[${i}]`);

      if (!type) {
        toast.error("الرجاء اختيار نوع السمة");
        return false;
      }

      if (type.includes("color") && !featuresData.get(`variations[${i}][color_id]`)) {
        toast.error(`الرجاء اختيار اللون للسمة رقم ${i + 1}`);
        return false;
      }
      if (type.includes("size") && !featuresData.get(`variations[${i}][size_id]`)) {
        toast.error(`الرجاء اختيار الحجم للسمة رقم ${i + 1}`);
        return false;
      }
      if (type.includes("unit") && !featuresData.get(`variations[${i}][unit_id]`)) {
        toast.error(`الرجاء اختيار المقاس للسمة رقم ${i + 1}`);
        return false;
      }
      if (!featuresData.get(`variations[${i}][price]`)) {
        toast.error(`الرجاء إدخال السعر للسمة رقم ${i + 1}`);
        return false;
      }
      if (!featuresData.get(`variations[${i}][qty]`)) {
        toast.error(`الرجاء إدخال الكمية للسمة رقم ${i + 1}`);
        return false;
      }

      i++;
    }

    return true;
  };

  // Submit handler for the form
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateVariations()) return;

    setIsLoading(true);

    try {
      const combinedFormData = new FormData();

      // Add main product image only if it changed
      if (mainImage) {
        combinedFormData.append("image", mainImage);
      }

      // Add ID for update operation
      // combinedFormData.append("id", id);

      // Add control group data
      if (controlGroupData) {
        // Copy all entries from controlGroupData to combinedFormData
        for (const [key, value] of controlGroupData.entries()) {
          combinedFormData.append(key, value);
        }
      }

      // Add global ranges data
      if (globalRangesData) {
        // Copy all entries from global ranges data to combinedFormData
        for (const [key, value] of globalRangesData.entries()) {
          combinedFormData.append(key, value);
        }
      }

      // Add features data
      if (featuresData) {
        // Copy all entries from featuresData to combinedFormData
        for (const [key, value] of featuresData.entries()) {
          combinedFormData.append(key, value);
        }
      }

      combinedFormData.append("sammary", "Empty Sammary");

      // Update existing product using PUT method
      await api.post(`/update-product/${id}`, combinedFormData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      toast.success("تم تحديث المنتج بنجاح");
      navigate("/products"); // Navigate back to products list after update
    } catch (error) {
      console.error("Error updating product:", error);
      toast.error("حدث خطأ أثناء تحديث المنتج");
    } finally {
      setIsLoading(false);
    }
  };

  if (fetchingProduct) {
    return <div className="loading-container">جاري تحميل بيانات المنتج...</div>;
  }

  return (
    <div className="create-product">
      <div className="product-container">
        <form onSubmit={handleSubmit}>
          <ProductHeader
            onImageChange={handleMainImage}
            existingProduct={productData}
          />
          <ControlGroup
            isMobile={isMobile}
            onDataChange={handleControlGroupData}
            onHasDiscountChange={setControlGroupHasDiscount}
            variationsHaveDiscount={variationsHaveDiscount}
            existingProduct={productData}
          />
          <Ranges
            isMobile={isMobile}
            isPadding={true}
            onRangesChange={handleGlobalRangesData}
            onRangesOnlyChange={setRangesActive}
            disabled={variationsActive}
            existingProduct={productData}
          />
          <Variations
            isMobile={isMobile}
            onVariationsChange={handleFeaturesData}
            onVariationsActiveChange={setVariationsActive}
            onVariationsHaveDiscountChange={setVariationsHaveDiscount}
            controlGroupHasDiscount={controlGroupHasDiscount}
            rangesOnly={rangesActive}
            existingProduct={productData}
          />
          <BlueButton
            text={isLoading ? "جاري التحديث..." : "تحديث المنتج"}
            size={isMobile ? "full" : "large"}
            variant="fill"
            type="submit"
            disabled={isLoading}
          />
        </form>
      </div>
    </div>
  );
};

export default UpdateProduct;
